<template>
  <v-container fluid>

    <PageTitle icon="fal fa-fw fa-clock">
      Session {{ session.id }}
    </PageTitle>

    <v-card class="mb-4">
      <v-simple-table>
        <template v-slot:default>
          <tbody>
          <tr v-if="canMangeAllerganDeviceSessions">
            <th>
              Moxie Call Interaction
            </th>
            <td>
              <div v-if="session.crm_object_id && session.crm_related_id">
                <a :href="`https://allergannewmaorg.lightning.force.com/lightning/r/${session.crm_related_object}/${session.crm_related_id}/view`"
                   target="_blank">Connected</a>

                <v-btn v-if="canDisconnectSessionFromMoxieCall"
                       @click.prevent="disconnect"
                       x-small
                       color="error"
                       class="ml-4">
                  Disconnect
                </v-btn>
              </div>
              <v-btn v-else
                     @click.prevent="editSession"
                     x-small>
                Connect
              </v-btn>
            </td>
          </tr>
          <tr>
            <th>
              Presentation
            </th>
            <td>
              {{ session.presentation.name }}
            </td>
          </tr>
          <tr>
            <th>
              Presentation Type
            </th>
            <td>
              {{ session.presentation.presentation_type }}
            </td>
          </tr>
          <tr>
            <th>
              Mode
            </th>
            <td>
              {{ session.live_mode ? 'Live Mode' : 'Test Mode' }}
            </td>
          </tr>
          <tr>
            <th>
              Started
            </th>
            <td>
              {{ session.started_at | datetime('YYYY-MM-DD h:mm:ss a') }}
            </td>
          </tr>
          <tr>
            <th>
              Ended
            </th>
            <td>
              <template v-if="session.ended_at">
                {{ session.ended_at | datetime('YYYY-MM-DD h:mm:ss a') }}
              </template>
            </td>
          </tr>
          <tr>
            <th>
              Duration
            </th>
            <td>
              <span v-if="session.duration > 60">
                About
              </span>
              <span>
                {{ session.duration_for_humans }}
              </span>
              <span v-if="session.duration > 60">
                ({{ session.duration | pluralize('second', true) }})
              </span>
            </td>
          </tr>
          <tr>
            <th>
              Closed Properly
            </th>
            <td>
              {{ session.closed_properly ? 'Yes' : 'No' }}
            </td>
          </tr>
          <tr>
            <th>
              Approximate Location
            </th>
            <td>
              <div v-if="session.ipAddress && session.ipAddress.geolocation">
                <div>
                  {{ session.ipAddress.geolocation.city }}, {{ session.ipAddress.geolocation.state }}, {{ session.ipAddress.geolocation.country }}
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <th>
              User
            </th>
            <td>
              {{ session.user.name }}
            </td>
          </tr>
          <tr>
            <th>
              Device Type
            </th>
            <td>
              {{ session.device.device_type }}
            </td>
          </tr>
          <tr v-if="sortedEntities?.length">
            <th>
              Associated with
            </th>
            <td>
              <ul class="list-unstyled">
                <li v-for="entity in sortedEntities"
                    :key="entity.id">
                  {{ entity.crm_related_name }} ({{ entity.crm_related_object }})
                </li>
              </ul>
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>

    <v-card>
      <v-card-title>
        <v-icon left>
          fal fa-fw fa-list
        </v-icon>
        Events
      </v-card-title>
      <v-data-table dense
                    :headers="headers"
                    :items="events"
                    :items-per-page="100">

        <template v-slot:item.event_at="{item: event}">
          {{ event.event_at | datetime('YYYY-MM-DD h:mm:ss a') }}
        </template>

        <template v-slot:item.duration="{item: event}">
          {{ event.duration_for_humans }}
        </template>

      </v-data-table>
    </v-card>

    <allergan-device-session-dialog v-if="dialog"
                                    :dialog="dialog"
                                    :session="session"
                                    @close="closeDialog"
                                    @updated="updated">
    </allergan-device-session-dialog>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import deviceEventService from '../../../../ml/services/deviceEventService';
import AllerganDeviceSessionDialog from './components/AllerganDeviceSessionDialog';
import DOMPurify from 'dompurify';
import {orderBy} from 'lodash';
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle, AllerganDeviceSessionDialog},
  props: {
    session: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      dialog: false,
      events: [],
    }
  },
  computed: {
    ...mapGetters([
      'impersonating', // MLD-3014
      'isAdmin',
      'isMember',
      'team',
      'user',
    ]),
    breadcrumbs() {
      return [
        {
          text: 'Sessions',
          disabled: false,
          exact: true,
          to: {name: `${this.$root.routePrefix}.device_sessions`},
        },
        {
          text: `Session ${this.session.id}`,
          disabled: true,
        },
      ]
    },
    canDisconnectSessionFromMoxieCall() {
      // MLD-3014 Only show the Disconnect button to specific users
      return this.session
        && this.session.crm_object
        && this.session.crm_object_id
        && this.session.crm_related_object
        && this.session.crm_related_id
        && this.team
        && this.team.id === 199
        && this.user
        && (this.impersonating || [3, 1940, 2265, 2560, 3545].includes(this.user.id)); // Mark, Sumit, Ayushi, Delicia, Bhuvana
    },
    canMangeAllerganDeviceSessions() { // MLD-3014
      return this.team
        && this.team.id === 199
        && this.user
        && this.user.is_full_user;
      // && [3, 1940, 2265, 2560, 3545].includes(this.user.id); // Mark, Sumit, Ayushi, Delicia, Bhuvana
    },
    headers() {
      return [
        {
          text: 'ID',
          value: 'id',
        },
        {
          text: 'Date/Time',
          value: 'event_at',
        },
        {
          text: 'Duration (seconds)',
          value: 'duration',
        },
        {
          text: 'Category',
          value: 'category',
        },
        {
          text: 'Action',
          value: 'action',
        },
        {
          text: 'Path',
          value: 'path',
        }
      ]
    },
    sortedEntities() { // MLD-5581
      if (this.session.entities) {
        return orderBy(this.session.entities.filter(c => !!c.crm_related_name), ['crm_related_slug', 'id'], ['asc', 'asc']);
      }
      return [];
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      deviceEventService.getSessionEvents(this.session.id)
        .then(data => {
          this.events = data;
        })
    },
    editSession() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
    },
    updated() {
      this.closeDialog()
      this.$emit('updated')
    },
    disconnect() {
      if (!confirm('Are you sure you want to disconnect this session from the Moxie Call Interaction?')) {
        return;
      }
      axios.delete(`/api/clients/agnma/device-sessions/${this.session.id}/disconnect`)
        .then(response => {
          setTimeout(() => {
            this.$emit('updated');
          }, 1000);
        })
        .catch(err => {
          this.$toast.error(`Error: ${DOMPurify.sanitize(err.response.data.message)}`);
        })
    }
  }
}
</script>
